/**
 * App APP_TO_WEB_Theme 下发的申请 seed（getByUserId / getListByUserId）。
 * applySeeded=true 时首屏可跳过对应接口；提交/刷新节点仍自行请求。
 */

export type ApplySeedType = 'NEW_APPLY' | 'ADD_ACCOUNT';

export type ApplySeedPage = 'open-progress' | 'add-account';

type ApplySeedState = {
  /** Theme 已处理过申请字段（含 applySeeded=false）或短等超时 */
  ready: boolean;
  applySeeded: boolean;
  applyType: ApplySeedType | null;
  /** NEW_APPLY：getByUserId 结果；null = 尚无申请（合法 seed） */
  applyInfo: any | null;
  /** ADD_ACCOUNT：getListByUserId 结果 */
  applyList: any[];
};

const state: ApplySeedState = {
  ready: false,
  applySeeded: false,
  applyType: null,
  applyInfo: null,
  applyList: [],
};

let readyResolvers: Array<() => void> = [];

const notifyReady = () => {
  state.ready = true;
  const resolvers = readyResolvers;
  readyResolvers = [];
  resolvers.forEach((r) => r());
};

const isRnWebView = () =>
  typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;

/**
 * Theme 灌入申请 seed。任意 Theme 到达都 mark ready（含未带 seed 的旧 App）。
 */
export const applyApplySeedFromTheme = (payload: Record<string, unknown> | null | undefined) => {
  const data = payload ?? {};
  const seeded = data.applySeeded === true;
  state.applySeeded = seeded;

  if (seeded) {
    const rawType = data.applyType;
    state.applyType =
      rawType === 'NEW_APPLY' || rawType === 'ADD_ACCOUNT' ? rawType : null;
    // null 合法：尚无申请
    state.applyInfo = (data.applyInfo as any) ?? null;
    state.applyList = Array.isArray(data.applyList) ? (data.applyList as any[]) : [];
  } else {
    state.applyType = null;
    state.applyInfo = null;
    state.applyList = [];
  }

  notifyReady();
};

/**
 * 首屏请求前短等 Theme。非 WebView 立即返回；已 ready 立即返回；否则等 Theme 或超时。
 */
export const waitForApplySeed = (timeoutMs = 400): Promise<void> => {
  if (!isRnWebView()) {
    if (!state.ready) notifyReady();
    return Promise.resolve();
  }
  if (state.ready) return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const finish = () => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      if (!state.ready) notifyReady();
      // 从等待队列移除（若尚未被 notifyReady 清空）
      readyResolvers = readyResolvers.filter((r) => r !== finish);
      resolve();
    };

    readyResolvers.push(finish);
    timer = setTimeout(finish, timeoutMs);
  });
};

export type ConsumeApplySeedResult =
  | { used: true; applyInfo: any | null; applyList?: undefined }
  | { used: true; applyList: any[]; applyInfo?: undefined }
  | { used: false };

/**
 * 按页面消费 seed；成功后清 applySeeded，避免提交后再用过期数据。
 */
export const consumeApplySeed = (page: ApplySeedPage): ConsumeApplySeedResult => {
  if (!state.applySeeded) return { used: false };

  if (page === 'open-progress' && state.applyType === 'NEW_APPLY') {
    const applyInfo = state.applyInfo;
    state.applySeeded = false;
    return { used: true, applyInfo };
  }

  if (page === 'add-account' && state.applyType === 'ADD_ACCOUNT') {
    const applyList = state.applyList ?? [];
    state.applySeeded = false;
    return { used: true, applyList };
  }

  return { used: false };
};

/** 调试 / 单测 */
export const peekApplySeed = () => ({ ...state });
